/**
 * useTrialAccount — Hook for managing the current user's trial account.
 * Reads from the `trial_accounts` table, provides helpers for updating mode/goals/status.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface TrialAccount {
  id: string;
  user_id: string;
  trial_type: "org" | "association";
  trial_mode: "demo_data" | "clean_start" | null;
  trial_status: "active" | "expired" | "converted" | "abandoned" | "archived";
  intended_conversion_target: "organization" | "association";
  provisioned_org_id: string | null;
  converted_entity_id: string | null;
  converted_entity_type: string | null;
  converted_at: string | null;
  org_name: string | null;
  org_type: string | null;
  member_count_estimate: string | null;
  source: string | null;
  selected_plan_interest: string | null;
  selected_goals: string[];
  activation_milestones: string[];
  activation_score: number;
  demo_data_seeded: boolean;
  demo_data_purged: boolean;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useTrialAccount() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["trial_account", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trial_accounts")
        .select("*")
        .eq("user_id", user!.id)
        .eq("trial_status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as TrialAccount | null;
    },
    enabled: !!user?.id,
  });

  const updateTrial = useMutation({
    mutationFn: async (updates: Partial<TrialAccount>) => {
      if (!query.data?.id) throw new Error("No active trial");
      const { error } = await supabase.from("trial_accounts").update(updates).eq("id", query.data.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trial_account", user?.id] });
    },
  });

  const createTrial = useMutation({
    mutationFn: async (input: {
      trial_type?: "org" | "association";
      provisioned_org_id: string;
      org_name?: string;
      org_type?: string;
    }) => {
      const { data, error } = await supabase
        .from("trial_accounts")
        .insert({
          user_id: user!.id,
          trial_type: input.trial_type || "org",
          intended_conversion_target: input.trial_type === "association" ? "association" : "organization",
          provisioned_org_id: input.provisioned_org_id,
          org_name: input.org_name,
          org_type: input.org_type,
          source: "free_trial",
        })
        .select("*")
        .single();
      if (error) throw error;
      return data as TrialAccount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trial_account", user?.id] });
    },
  });

  return {
    trial: query.data ?? null,
    isLoading: query.isLoading,
    isTrialUser: !!query.data,
    isDemoMode: query.data?.trial_mode === "demo_data" && !query.data?.demo_data_purged,
    updateTrial,
    createTrial,
  };
}
